import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSession } from '../session/useSession.js';
import type {
  AnalyticsConsentChoice,
  AnalyticsConsentCurrent,
  AnalyticsConsentPort,
} from './AnalyticsConsentPort.js';

const REFRESH_INTERVAL_MS = 60_000;

export type AnalyticsConsentResolution =
  | Readonly<{ kind: 'notMember' }>
  | Readonly<{ kind: 'loading'; accountKey: string }>
  | Readonly<{ kind: 'unavailable'; accountKey: string }>
  | Readonly<{ kind: 'ready'; accountKey: string; current: AnalyticsConsentCurrent }>;

type SaveError = 'failed' | 'stale';

export interface AnalyticsConsentContextValue {
  readonly resolution: AnalyticsConsentResolution;
  /**
   * Browser collection is permitted only for this current member and only when
   * a successful authoritative choice read says accepted. A failed local save
   * remains fail-closed until a later successful choice explicitly resolves it.
   */
  readonly captureAllowed: boolean;
  /**
   * Whether a capture owner mounted now may read facts from the document that
   * loaded before it. Only a document whose every authoritative resolution has
   * allowed capture keeps them. An in-page acceptance, or a later read that
   * accepts after any refusing, unknown or unavailable resolution, starts a new
   * capture generation that must not reuse that document's URL, referrer, or
   * timing.
   */
  readonly mayReadDocumentEntry: boolean;
  readonly savingChoice: AnalyticsConsentChoice | null;
  /** The choice that was not authoritatively saved, if any. */
  readonly unsavedChoice: AnalyticsConsentChoice | null;
  readonly saveError: SaveError | null;
  choose(choice: AnalyticsConsentChoice): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Hides the optional initial prompt for this signed-in account and notice
   * version only. This is React-local UI state, not a persisted choice.
   */
  dismissInitialNotice(): void;
  /** Whether the initial prompt was dismissed without choosing during this visit. */
  readonly initialNoticeDismissed: boolean;
  /** Fail closed after the capture API reports that this choice is no longer current. */
  captureRejected(): void;
}

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null);

export function accountKeyForMember(state: ReturnType<typeof useSession>['state']): string | null {
  if (state.kind !== 'member') {
    return null;
  }
  // This key is React-local request invalidation state only. It is never sent
  // to Analytics, persisted, or used as a consent authority.
  return `${state.memberNumber}:${state.email}`;
}

/**
 * The authoritative part of the capture decision. `captureAllowed` adds the
 * transient save state on top of it.
 */
function resolutionAllowsCapture(
  resolution: AnalyticsConsentResolution,
  accountKey: string | null,
): boolean {
  return (
    resolution.kind === 'ready' &&
    resolution.accountKey === accountKey &&
    resolution.current.status === 'accepted'
  );
}

function isStaleChoiceError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('analytics-consent-stale');
}

export interface AnalyticsConsentProviderProps {
  readonly port: AnalyticsConsentPort;
  readonly children: ReactNode;
}

/**
 * Owns the browser's short-lived view of the server-authoritative choice.
 * Missing, stale and unreadable choices all deny capture. No consent is kept in
 * browser storage; refreshes deliberately re-read the authenticated server API.
 */
export function AnalyticsConsentProvider({
  port,
  children,
}: AnalyticsConsentProviderProps): JSX.Element {
  const session = useSession();
  const accountKey = accountKeyForMember(session.state);
  const [resolution, setResolution] = useState<AnalyticsConsentResolution>(() =>
    accountKey === null ? { kind: 'notMember' } : { kind: 'loading', accountKey },
  );
  const [savingChoice, setSavingChoice] = useState<AnalyticsConsentChoice | null>(null);
  const [unsavedChoice, setUnsavedChoice] = useState<AnalyticsConsentChoice | null>(null);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [mayReadDocumentEntry, setMayReadDocumentEntry] = useState(true);
  const [dismissedNoticeKey, setDismissedNoticeKey] = useState<string | null>(null);
  const readGenerationRef = useRef(0);
  const accountKeyRef = useRef(accountKey);

  // An account transition must deny capture in the render that observes it;
  // this committed ref also prevents a completed request for the former member
  // from writing a resolution for the next member.
  useLayoutEffect(() => {
    accountKeyRef.current = accountKey;
    setSavingChoice(null);
    setUnsavedChoice(null);
    setSaveError(null);
    setDismissedNoticeKey(null);
  }, [accountKey]);

  // react-compiler-exception: refresh identity keys authoritative-read and focus/timer effects
  const refresh = useCallback(async (): Promise<void> => {
    const requestedAccountKey = accountKey;
    const generation = ++readGenerationRef.current;
    if (requestedAccountKey === null) {
      setResolution({ kind: 'notMember' });
      setSavingChoice(null);
      setUnsavedChoice(null);
      setSaveError(null);
      return;
    }

    setResolution((previous) =>
      (previous.kind === 'ready' || previous.kind === 'loading') &&
      previous.accountKey === requestedAccountKey
        ? previous
        : { kind: 'loading', accountKey: requestedAccountKey },
    );
    try {
      const current = await port.current();
      if (
        generation !== readGenerationRef.current ||
        accountKeyRef.current !== requestedAccountKey
      ) {
        return;
      }
      const next: AnalyticsConsentResolution = {
        kind: 'ready',
        accountKey: requestedAccountKey,
        current,
      };
      if (!resolutionAllowsCapture(next, requestedAccountKey)) {
        // This document was viewed under a choice that did not allow capture.
        // A later acceptance must not attribute it or reuse its load timing.
        setMayReadDocumentEntry(false);
      }
      // An unchanged re-read keeps the previous object so consumers do not
      // re-render on every poll or focus. Every field of the current choice is
      // compared.
      setResolution((previous) =>
        previous.kind === 'ready' &&
        previous.accountKey === requestedAccountKey &&
        previous.current.status === current.status &&
        previous.current.noticeVersion === current.noticeVersion &&
        previous.current.revision === current.revision
          ? previous
          : next,
      );
    } catch {
      if (
        generation !== readGenerationRef.current ||
        accountKeyRef.current !== requestedAccountKey
      ) {
        return;
      }
      setMayReadDocumentEntry(false);
      setResolution({ kind: 'unavailable', accountKey: requestedAccountKey });
    }
  }, [accountKey, port]);

  // react-compiler-exception: context action identity gates every consent consumer's re-render
  const captureRejected = useCallback((): void => {
    if (accountKey === null) {
      return;
    }
    // The server is authoritative. Stop the mounted browser owner before the
    // follow-up current read completes, so a rejected in-flight capture cannot
    // authorize another browser-side attempt.
    setMayReadDocumentEntry(false);
    setResolution({ kind: 'unavailable', accountKey });
    void refresh();
  }, [accountKey, refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (accountKey === null) {
      return;
    }
    const refreshOnFocus = (): void => {
      void refresh();
    };
    window.addEventListener('focus', refreshOnFocus);
    const interval = window.setInterval(refreshOnFocus, REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      window.clearInterval(interval);
    };
  }, [accountKey, refresh]);

  // react-compiler-exception: context action identity gates every consent consumer's re-render
  const choose = useCallback(
    async (choice: AnalyticsConsentChoice): Promise<void> => {
      if (resolution.kind !== 'ready' || accountKey === null) {
        return;
      }
      const requestedAccountKey = accountKey;
      // A refresh can begin while this write is in flight. Invalidate its
      // snapshot now, and again when the authoritative mutation completes, so
      // neither an older nor concurrent read can restore a withdrawn choice.
      readGenerationRef.current += 1;
      setSavingChoice(choice);
      setUnsavedChoice(null);
      setSaveError(null);
      try {
        const current = await port.choose({
          choice,
          displayedNoticeVersion: resolution.current.noticeVersion,
          expectedRevision: resolution.current.revision,
        });
        if (accountKeyRef.current !== requestedAccountKey) {
          return;
        }
        readGenerationRef.current += 1;
        const next: AnalyticsConsentResolution = {
          kind: 'ready',
          accountKey: requestedAccountKey,
          current,
        };
        if (choice === 'accepted' || !resolutionAllowsCapture(next, requestedAccountKey)) {
          // The current document predates the affirmative action. Collection
          // may begin now, but it must not attribute that earlier document or
          // reuse its load timing as if they were observed after consent. A
          // saved choice that does not allow capture latches the same way.
          setMayReadDocumentEntry(false);
        }
        setResolution(next);
        setSavingChoice(null);
      } catch (error: unknown) {
        if (accountKeyRef.current !== requestedAccountKey) {
          return;
        }
        readGenerationRef.current += 1;
        // Fail closed. In particular, a failed withdrawal cannot leave the
        // previous accepted local state authorizing new browser collection.
        setSavingChoice(null);
        setUnsavedChoice(choice);
        setSaveError(isStaleChoiceError(error) ? 'stale' : 'failed');
        void refresh();
      }
    },
    [accountKey, port, refresh, resolution],
  );

  const currentNoticeKey =
    resolution.kind === 'ready' && resolution.current.status === 'unknown'
      ? `${resolution.accountKey}:${resolution.current.noticeVersion}`
      : null;
  const initialNoticeDismissed =
    currentNoticeKey !== null && dismissedNoticeKey === currentNoticeKey;

  // react-compiler-exception: context action identity gates every consent consumer's re-render
  const dismissInitialNotice = useCallback((): void => {
    if (currentNoticeKey !== null) {
      setDismissedNoticeKey(currentNoticeKey);
    }
  }, [currentNoticeKey]);

  const captureAllowed =
    resolutionAllowsCapture(resolution, accountKey) &&
    savingChoice === null &&
    unsavedChoice === null &&
    saveError === null;

  // react-compiler-exception: context value identity gates every consent consumer's re-render
  const value = useMemo<AnalyticsConsentContextValue>(
    () => ({
      resolution,
      captureAllowed,
      mayReadDocumentEntry,
      savingChoice,
      unsavedChoice,
      saveError,
      choose,
      refresh,
      dismissInitialNotice,
      initialNoticeDismissed,
      captureRejected,
    }),
    [
      resolution,
      captureAllowed,
      mayReadDocumentEntry,
      savingChoice,
      unsavedChoice,
      saveError,
      choose,
      refresh,
      dismissInitialNotice,
      initialNoticeDismissed,
      captureRejected,
    ],
  );

  return (
    <AnalyticsConsentContext.Provider value={value}>{children}</AnalyticsConsentContext.Provider>
  );
}

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const context = useOptionalAnalyticsConsent();
  if (context === null) {
    throw new Error('useAnalyticsConsent must be used within an AnalyticsConsentProvider');
  }
  return context;
}

/**
 * Shell-only affordances may render in isolated CMS preview tests that do not
 * compose member capture. They stay absent there; production member routes use
 * the strict hook above through the mounted provider.
 */
export function useOptionalAnalyticsConsent(): AnalyticsConsentContextValue | null {
  return useContext(AnalyticsConsentContext);
}
